import { GamesServiceAPI } from '@API/data-services/games-service'
import { RootState } from '@AppState/redux-store'
import { Routes } from '@Components/main-app/types'
import { readErrorMessage } from '@Components/util/utils'
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { Container, Flex, Heading, Section, Skeleton, Text } from '@radix-ui/themes'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import { SortableGameRow } from './components/sortable-game-row'
import { GAMES_LOAD_FAILED_MESSAGE, REORDER_FAILED_MESSAGE } from './constants'
import { EditableGame } from './types'
import { useEditableGames } from './use-editable-games'
import { editableGamesQueryKey } from './utils'
import './styles.scss'

export const GamesAdmin = () => {
  const { activeCustomerId } = useSelector((state: RootState) => state.users)
  const queryClient = useQueryClient()
  const queryKey = editableGamesQueryKey(activeCustomerId)
  const { data: games, isLoading, error: loadError } = useEditableGames()
  const [reorderError, setReorderError] = useState<string | null>(null)

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  const reorderMutation = useMutation({
    // Serialises repeated drags: two in quick succession would otherwise answer in any order and
    // leave the list showing whichever landed last.
    scope: { id: queryKey.join(':') },
    // The whole list goes in one call, so a drag that renumbers several games cannot half-apply.
    mutationFn: (ordered: EditableGame[]) =>
      GamesServiceAPI.setGameDisplayOrder(
        ordered.map((game, index) => ({ gameId: game.id, displayOrder: index })),
      ),
    onMutate: async (ordered: EditableGame[]) => {
      // An in-flight refetch would otherwise land after this write and restore the pre-drag order.
      await queryClient.cancelQueries({ queryKey })
      const previousGames = queryClient.getQueryData<EditableGame[]>(queryKey)

      queryClient.setQueryData(
        queryKey,
        ordered.map((game, index) => ({ ...game, displayOrder: index })),
      )

      return { previousGames }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey })
    },
    onError: async (error: unknown, _ordered, context) => {
      setReorderError(await readErrorMessage(error, REORDER_FAILED_MESSAGE))
      if (context?.previousGames) {
        queryClient.setQueryData(queryKey, context.previousGames)
      }
      queryClient.invalidateQueries({ queryKey })
    },
  })

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event

    if (!games || !over || active.id === over.id) {
      return
    }

    const oldIndex = games.findIndex((game) => game.id === active.id)
    const newIndex = games.findIndex((game) => game.id === over.id)
    const reordered = arrayMove(games, oldIndex, newIndex)

    setReorderError(null)
    reorderMutation.mutate(reordered)
  }

  return (
    <Container>
      <Section>
        <Flex direction="column" gap="4">
          <Flex justify="between" align="center">
            <Heading size="6">Manage Games</Heading>
            <Link to={Routes.PLAYSLIP_THEMES}>
              <Text size="2">Playslip themes →</Text>
            </Link>
          </Flex>

          <Text size="2" color="gray">
            Drag a game to change where it appears in the playslip picker.
          </Text>

          {loadError && (
            <Text size="2" color="red" data-testid="games-load-error">
              {GAMES_LOAD_FAILED_MESSAGE}
            </Text>
          )}

          {reorderError && (
            <Text size="2" color="red" data-testid="games-reorder-error">
              {reorderError}
            </Text>
          )}

          {isLoading ? (
            <Skeleton loading={true}>
              <Flex direction="column" gap="2" data-testid="games-loading-skeleton">
                <div className="skeleton-card" />
                <div className="skeleton-card" />
                <div className="skeleton-card" />
              </Flex>
            </Skeleton>
          ) : games && games.length > 0 ? (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}>
              <SortableContext
                items={games.map((game) => game.id)}
                strategy={verticalListSortingStrategy}>
                <Flex direction="column" gap="2">
                  {games.map((game) => (
                    <SortableGameRow key={game.id} game={game} />
                  ))}
                </Flex>
              </SortableContext>
            </DndContext>
          ) : (
            // Only when the load succeeded — otherwise a failure reads as a customer with no games.
            !loadError && (
              <Flex direction="column" align="center" gap="2" py="8">
                <Text size="4" weight="medium">
                  No editable games
                </Text>
                <Text size="2" color="gray">
                  Draw and rapid games appear here once they arrive from the feed
                </Text>
              </Flex>
            )
          )}
        </Flex>
      </Section>
    </Container>
  )
}
